/**
 * Sistema visual — fonte única de tokens (claro/escuro) e derivação da marca de cada empresa.
 * A interface instala estes valores como variáveis CSS; os testes validam o contraste (WCAG 2.2).
 */

export type ThemeMode = 'light' | 'dark';

export interface BrandInput {
  primary: string; // cor de ação/identificação (botões principais, seleção)
  accent?: string | null; // acentos e detalhes (decorativo)
  highlight?: string | null; // destaques pontuais (decorativo)
}

// ── Cor: conversões e contraste ─────────────────────────────────────────────

export function isHexColor(v: unknown): v is string {
  return typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v);
}

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex([r, g, b]: [number, number, number]): string {
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

const toLinear = (c: number) => {
  const s = c / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};
const fromLinear = (l: number) => 255 * (l <= 0.0031308 ? 12.92 * l : 1.055 * l ** (1 / 2.4) - 0.055);

export function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(toLinear);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
  const [l1, l2] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (l1 + 0.05) / (l2 + 0.05);
}

/** Mistura duas cores sólidas (t = peso de b). */
export function mix(a: string, b: string, t: number): string {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  return rgbToHex([0, 1, 2].map((i) => ca[i] + (cb[i] - ca[i]) * t) as [number, number, number]);
}

// OKLab/OKLCH — ajuste perceptual de luminosidade preservando o matiz da marca.
function rgbToOklab(hex: string): [number, number, number] {
  const [r, g, b] = hexToRgb(hex).map(toLinear);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s, 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s, 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s];
}

function oklabToHex([L, a, b]: [number, number, number]): string {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const rgb: [number, number, number] = [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
  return rgbToHex(rgb.map((v) => fromLinear(Math.max(0, Math.min(1, v)))) as [number, number, number]);
}

/** Mesma cor com outra luminosidade perceptual (0–1), limitando o croma para caber no sRGB. */
export function withLightness(hex: string, L: number, maxChroma = 0.16): string {
  const [, a, b] = rgbToOklab(hex);
  const c = Math.hypot(a, b);
  const h = Math.atan2(b, a);
  let chroma = Math.min(c, maxChroma);
  for (let i = 0; i < 20; i++) {
    const out = oklabToHex([L, chroma * Math.cos(h), chroma * Math.sin(h)]);
    const back = rgbToOklab(out);
    if (Math.abs(back[0] - L) < 0.01) return out;
    chroma *= 0.85;
  }
  return oklabToHex([L, 0, 0]);
}

export function lightnessOf(hex: string): number {
  return rgbToOklab(hex)[0];
}

/** Escolhe texto (claro ou escuro) com o melhor contraste sobre um fundo. */
export function onColor(bg: string, light = '#FFFFFF', dark = '#0E1A19'): string {
  return contrastRatio(bg, light) >= contrastRatio(bg, dark) ? light : dark;
}

/** Ajusta a cor até atingir o contraste mínimo sobre o fundo (escurecendo ou clareando). */
export function ensureContrast(fg: string, bg: string, min: number): string {
  if (contrastRatio(fg, bg) >= min) return fg;
  const darker = relativeLuminance(bg) > 0.4;
  let L = lightnessOf(fg);
  for (let i = 0; i < 60; i++) {
    L = darker ? L - 0.012 : L + 0.012;
    const c = withLightness(fg, Math.max(0, Math.min(1, L)));
    if (contrastRatio(c, bg) >= min) return c;
  }
  return darker ? '#000000' : '#FFFFFF';
}

// ── Tokens base ─────────────────────────────────────────────────────────────

export interface ThemeTokens {
  [name: string]: string;
}

/** Neutros e estados. Valores de texto validados ≥ 4,5:1 sobre fundo e superfície. */
export const BASE_TOKENS: Record<ThemeMode, ThemeTokens> = {
  light: {
    bg: '#F5F7F6',
    'bg-tint': '#EEF2F0',
    surface: '#FFFFFF',
    'surface-2': '#FAFBFB',
    'surface-sunken': '#F0F3F2',
    'surface-hover': 'rgba(23, 43, 41, 0.045)',
    'surface-active': 'rgba(23, 43, 41, 0.08)',
    elevated: '#FFFFFF',
    overlay: 'rgba(14, 26, 25, 0.28)',
    border: '#DFE7E4',
    'border-strong': '#C9D3D0',
    separator: 'rgba(23, 43, 41, 0.09)',
    text: '#172B29',
    'text-2': '#5E6E6B',
    'text-3': '#626F6B',
    'text-inverse': '#FFFFFF',
    success: '#1F7A3A',
    'success-soft': 'rgba(31, 122, 58, 0.11)',
    warning: '#8F4A00',
    'warning-soft': 'rgba(242, 140, 0, 0.15)',
    danger: '#B42318',
    'danger-soft': 'rgba(212, 43, 31, 0.09)',
    info: '#1C5FB8',
    'info-soft': 'rgba(28, 95, 184, 0.1)',
    violet: '#5B3FC4',
    'violet-soft': 'rgba(110, 80, 220, 0.11)',
    // vidro: translucidez moderada + borda iluminada + sombra suave
    'glass-bg': 'rgba(255, 255, 255, 0.72)',
    'glass-bg-strong': 'rgba(255, 255, 255, 0.86)',
    'glass-fallback': '#FBFCFC',
    'glass-border': 'rgba(255, 255, 255, 0.75)',
    'glass-edge': 'rgba(23, 43, 41, 0.08)',
    'glass-highlight': 'inset 0 1px 0 rgba(255, 255, 255, 0.9), inset 0 0 0 1px rgba(255, 255, 255, 0.35)',
    'shadow-xs': '0 0 0 1px rgba(23, 43, 41, 0.05), 0 1px 2px rgba(23, 43, 41, 0.04)',
    'shadow-sm': '0 0 0 1px rgba(23, 43, 41, 0.055), 0 1px 3px rgba(23, 43, 41, 0.06)',
    'shadow-md': '0 0 0 1px rgba(23, 43, 41, 0.06), 0 8px 24px -8px rgba(23, 43, 41, 0.14)',
    'shadow-lg': '0 0 0 1px rgba(23, 43, 41, 0.07), 0 24px 64px -16px rgba(23, 43, 41, 0.28)',
    'shadow-glass': '0 0 0 1px rgba(23, 43, 41, 0.06), 0 10px 30px -10px rgba(23, 43, 41, 0.18)',
    'chart-grid': 'rgba(23, 43, 41, 0.08)',
    'series-1': '#2A78D6',
    'series-2': '#EB6834',
  },
  dark: {
    bg: '#0C1312',
    'bg-tint': '#101A19',
    surface: '#151E1D',
    'surface-2': '#192322',
    'surface-sunken': '#111918',
    'surface-hover': 'rgba(233, 244, 241, 0.055)',
    'surface-active': 'rgba(233, 244, 241, 0.09)',
    elevated: '#1C2726',
    overlay: 'rgba(0, 0, 0, 0.55)',
    border: '#263332',
    'border-strong': '#34433F',
    separator: 'rgba(233, 244, 241, 0.08)',
    text: '#EEF4F2',
    'text-2': '#A9B8B4',
    'text-3': '#8E9E9A',
    'text-inverse': '#0C1312',
    success: '#5FD38A',
    'success-soft': 'rgba(95, 211, 138, 0.13)',
    warning: '#FFB454',
    'warning-soft': 'rgba(255, 180, 84, 0.14)',
    danger: '#FF7B72',
    'danger-soft': 'rgba(255, 123, 114, 0.14)',
    info: '#79B4FF',
    'info-soft': 'rgba(121, 180, 255, 0.14)',
    violet: '#B9AAFF',
    'violet-soft': 'rgba(185, 170, 255, 0.14)',
    'glass-bg': 'rgba(24, 34, 33, 0.66)',
    'glass-bg-strong': 'rgba(24, 34, 33, 0.84)',
    'glass-fallback': '#18211F',
    'glass-border': 'rgba(255, 255, 255, 0.08)',
    'glass-edge': 'rgba(0, 0, 0, 0.4)',
    'glass-highlight': 'inset 0 1px 0 rgba(255, 255, 255, 0.07), inset 0 0 0 1px rgba(255, 255, 255, 0.04)',
    'shadow-xs': '0 0 0 1px rgba(255, 255, 255, 0.05)',
    'shadow-sm': '0 0 0 1px rgba(255, 255, 255, 0.06), 0 1px 3px rgba(0, 0, 0, 0.4)',
    'shadow-md': '0 0 0 1px rgba(255, 255, 255, 0.07), 0 10px 28px -8px rgba(0, 0, 0, 0.6)',
    'shadow-lg': '0 0 0 1px rgba(255, 255, 255, 0.08), 0 28px 72px -12px rgba(0, 0, 0, 0.75)',
    'shadow-glass': '0 0 0 1px rgba(255, 255, 255, 0.06), 0 12px 32px -10px rgba(0, 0, 0, 0.6)',
    'chart-grid': 'rgba(233, 244, 241, 0.08)',
    'series-1': '#3987E5',
    'series-2': '#D95926',
  },
};

/** Identidade neutra da plataforma (admin global e empresas sem marca própria). */
export const PLATFORM_BRAND: BrandInput = { primary: '#1F2933', accent: '#3B7BF6', highlight: '#3B7BF6' };

/** Tokens de marca derivados para um modo. Garante contraste do texto sobre o botão principal. */
export function brandTokens(brand: BrandInput, mode: ThemeMode): ThemeTokens {
  const base = BASE_TOKENS[mode];
  const surface = base.surface;
  const accentIn = isHexColor(brand.accent) ? brand.accent : brand.primary;
  const highlightIn = isHexColor(brand.highlight) ? brand.highlight : accentIn;
  let primary = brand.primary;
  if (mode === 'dark') {
    // Cores escuras da marca são elevadas mantendo o matiz, para continuar legíveis no escuro.
    primary = lightnessOf(primary) < 0.7 ? withLightness(primary, 0.8, 0.09) : primary;
  }
  const onPrimary = onColor(primary);
  const hover = mode === 'light' ? withLightness(primary, Math.max(0, lightnessOf(primary) - 0.05)) : withLightness(primary, Math.min(1, lightnessOf(primary) + 0.05), 0.09);
  const press = mode === 'light' ? withLightness(primary, Math.max(0, lightnessOf(primary) - 0.09)) : withLightness(primary, Math.min(1, lightnessOf(primary) - 0.04), 0.09);
  // Texto na cor da marca (links, item selecionado): ≥ 4,5:1 sobre a superfície e a área suave.
  const primaryText = ensureContrast(primary, mix(surface, primary, 0.14), 4.6);
  const accentStrong = ensureContrast(accentIn, surface, 3);
  return {
    primary,
    'primary-hover': hover,
    'primary-press': press,
    'on-primary': onPrimary,
    'primary-text': primaryText,
    'primary-soft': hexToRgba(primary, mode === 'light' ? 0.09 : 0.14),
    'primary-soft-2': hexToRgba(primary, mode === 'light' ? 0.15 : 0.22),
    focus: hexToRgba(primary, mode === 'light' ? 0.28 : 0.42),
    accent: accentIn,
    'accent-strong': accentStrong,
    'accent-soft': hexToRgba(accentIn, mode === 'light' ? 0.16 : 0.18),
    highlight: highlightIn,
    'highlight-soft': hexToRgba(highlightIn, mode === 'light' ? 0.14 : 0.18),
    'backdrop-1': hexToRgba(primary, mode === 'light' ? 0.1 : 0.14),
    'backdrop-2': hexToRgba(accentIn, mode === 'light' ? 0.12 : 0.1),
    'backdrop-3': hexToRgba(highlightIn, mode === 'light' ? 0.06 : 0.06),
  };
}

export function hexToRgba(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** CSS completo de um tema: tokens base + marca, para :root e [data-theme=dark]. */
export function themeCss(brand: BrandInput = PLATFORM_BRAND): string {
  const block = (mode: ThemeMode) =>
    Object.entries({ ...BASE_TOKENS[mode], ...brandTokens(brand, mode) })
      .map(([k, v]) => `--${k}:${v};`)
      .join('');
  return `:root{color-scheme:light;${block('light')}}:root[data-theme="dark"]{color-scheme:dark;${block('dark')}}`;
}

/** Escalas do sistema (consumidas pelo CSS e documentadas aqui). */
export const SCALE = {
  space: [0, 4, 8, 12, 16, 20, 24, 32, 40, 48, 64],
  radius: { control: 12, card: 18, floating: 22, pill: 999 },
  motion: { fast: 160, base: 220, panel: 280, ease: 'cubic-bezier(0.2, 0.8, 0.2, 1)', spring: { type: 'spring', stiffness: 520, damping: 40, mass: 0.9 } },
  blur: { glass: 18, strong: 24 },
} as const;
